import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';

export type ConfigureModuleCardProps = {
  title: string;
  description: string;
  icon: ReactNode;
  onOpen?: () => void;
  openDisabled?: boolean;
};

export const ConfigureModuleCard = ({
  title,
  description,
  icon,
  onOpen,
  openDisabled = false,
}: ConfigureModuleCardProps) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: t => `1px solid ${t.palette.divider}`,
        bgcolor: t =>
          t.palette.mode === 'dark'
            ? t.palette.grey[900]
            : t.palette.grey[50],
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? `0 4px 10px ${alpha(theme.palette.common.black, 0.28)}`
              : `0 4px 10px ${alpha(theme.palette.common.black, 0.1)}`,
        },
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: 3,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" component="h2" fontWeight={600}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ flex: 1, mt: -0.5 }}
        >
          {description}
        </Typography>
        <Button
          variant="outlined"
          fullWidth
          onClick={onOpen}
          disabled={openDisabled || !onOpen}
          sx={{
            mt: 4,
            borderRadius: 1.5,
            bgcolor: 'common.white',
            color: 'primary.main',
            borderColor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderColor: 'primary.main',
            },
          }}
        >
          Open
        </Button>
      </CardContent>
    </Card>
  );
};
